import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Properties } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { Search, SlidersHorizontal, X } from "lucide-react";

const BUDGETS = [
  { value: "any", label: "Any Budget" },
  { value: "under_2cr", label: "Under ₹2 Cr" },
  { value: "2_5cr", label: "₹2 Cr – ₹5 Cr" },
  { value: "5_10cr", label: "₹5 Cr – ₹10 Cr" },
  { value: "above_10cr", label: "Above ₹10 Cr" },
];

const TYPES = ["All", "Showroom", "Bank", "QSR", "Retail", "F&B", "Office"];
const INDUSTRIES = ["All", "Banking", "Jewelry", "QSR", "Retail Fashion", "F&B", "Electronics Retail"];
const SORTS = [
  { value: "roi_desc", label: "Highest ROI" },
  { value: "roi_asc", label: "Lowest ROI" },
  { value: "price_asc", label: "Price · Low → High" },
  { value: "price_desc", label: "Price · High → Low" },
  { value: "rent_desc", label: "Highest Rent" },
];

export default function AllProperties() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const city = params.get("city") || "all";
  const budget = params.get("budget") || "any";
  const type = params.get("property_type") || "all";
  const industry = params.get("tenant_industry") || "all";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "roi_desc";

  useEffect(() => {
    Properties.cities().then((d) => setCities(d.cities || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (city !== "all") p.city = city;
    if (budget !== "any") p.budget = budget;
    if (type !== "all") p.property_type = type;
    if (industry !== "all") p.tenant_industry = industry;
    if (q) p.q = q;
    if (sort) p.sort = sort;
    Properties.list(p).then((d) => setItems(d.items || [])).finally(() => setLoading(false));
  }, [city, budget, type, industry, q, sort]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all" || v === "any" || v === "") next.delete(k); else next.set(k, v);
    setParams(next);
  };

  const clearAll = () => setParams({});

  return (
    <div className="container-zelo py-12 md:py-16" data-testid="all-properties-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="label-tiny text-brand">All Pre-Leased Listings</div>
          <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2">Browse every signed lease.</h1>
          <p className="mt-2 text-neutral-500">Verified, brand-tenanted, rent-yielding commercial assets across India.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              placeholder="Search tenant, city, area…"
              defaultValue={q}
              onKeyDown={(e) => { if (e.key === "Enter") setParam("q", e.currentTarget.value); }}
              className="w-full pl-9 pr-3 py-2.5 rounded-full border border-neutral-200 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-sm"
              data-testid="properties-search-input"
            />
          </div>
          <button onClick={() => setShowMobileFilters(true)} className="md:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-neutral-200 text-sm font-semibold" data-testid="mobile-filters-open">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Filters sidebar */}
        <aside className={`md:col-span-3 ${showMobileFilters ? "fixed inset-0 z-40 bg-white p-6 overflow-auto" : "hidden md:block"}`} data-testid="filters-sidebar">
          {showMobileFilters && (
            <div className="flex justify-between items-center mb-6 md:hidden">
              <div className="font-heading text-xl">Filters</div>
              <button onClick={() => setShowMobileFilters(false)}><X className="w-5 h-5" /></button>
            </div>
          )}
          <div className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-6 sticky top-24">
            <FilterGroup label="Sort By" testId="filter-sort">
              <select value={sort} onChange={(e) => setParam("sort", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm outline-none focus:border-brand">
                {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </FilterGroup>
            <FilterGroup label="City" testId="filter-city">
              <select value={city} onChange={(e) => setParam("city", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm outline-none focus:border-brand">
                <option value="all">All Cities</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FilterGroup>
            <FilterGroup label="Budget" testId="filter-budget">
              <select value={budget} onChange={(e) => setParam("budget", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm outline-none focus:border-brand">
                {BUDGETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </FilterGroup>
            <FilterGroup label="Property Type" testId="filter-type">
              <div className="flex flex-wrap gap-2">
                {TYPES.map((t) => (
                  <button key={t} onClick={() => setParam("property_type", t === "All" ? "all" : t)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${(type === t || (type === "all" && t === "All")) ? "bg-brand text-white border-brand" : "border-neutral-200 hover:border-brand hover:text-brand"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </FilterGroup>
            <FilterGroup label="Tenant Industry" testId="filter-industry">
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((t) => (
                  <button key={t} onClick={() => setParam("tenant_industry", t === "All" ? "all" : t)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${(industry === t || (industry === "all" && t === "All")) ? "bg-brand text-white border-brand" : "border-neutral-200 hover:border-brand hover:text-brand"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </FilterGroup>
            <button onClick={clearAll} className="w-full text-sm text-neutral-500 hover:text-brand transition" data-testid="clear-filters-btn">Clear all filters</button>
          </div>
        </aside>

        {/* Results */}
        <div className="md:col-span-9">
          <div className="text-sm text-neutral-500 mb-5" data-testid="results-count">
            {loading ? "Loading…" : `${items.length} propert${items.length === 1 ? "y" : "ies"} found`}
          </div>
          {!loading && items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-neutral-200 p-12 text-center">
              <div className="font-heading text-2xl font-medium">No matching properties.</div>
              <div className="mt-2 text-neutral-500">Try clearing some filters or expanding your budget range.</div>
              <button onClick={clearAll} className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold">Clear Filters</button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {items.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="label-tiny mb-2">{label}</div>
      {children}
    </div>
  );
}
